import React, { ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, useMediaQuery, useTheme, Divider, IconButton, Box } from '@mui/material';
import type { ButtonProps, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface CustomDialogWithContentProps {
  title: string;
  content: ReactNode; // JSX content for the dialog body
  actionButtonText: string;
  onAction: () => void; // Function to call when action button is clicked
  onCancel: () => void; // Function to call when cancel button is clicked
  open: boolean; // Whether the dialog is open or not
  fullScreenOnMobile?: boolean;
  stickyActions?: boolean;
  actionButtonColor?: ButtonProps['color'];
  actionButtonVariant?: ButtonProps['variant'];
  actionButtonSx?: SxProps;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

const CustomDialogWithContent: React.FC<CustomDialogWithContentProps> = ({
  title,
  content,
  actionButtonText,
  onAction,
  onCancel,
  open,
  fullScreenOnMobile = false,
  stickyActions = false,
  actionButtonColor = 'error',
  actionButtonVariant = 'text',
  actionButtonSx,
  maxWidth = 'sm',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="custom-dialog-title"
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreenOnMobile && isMobile}
    >
      <DialogTitle
        id="custom-dialog-title"
        sx={{ textAlign: { xs: 'center', sm: 'left' } }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }} />
          <Box sx={{ flex: 1, textAlign: 'center' }}>{title}</Box>
          <IconButton onClick={onCancel} aria-label="Close" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 2 }}>
        {content}
      </DialogContent>
      <DialogActions
        sx={
          stickyActions
            ? {
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1,
              gap: 0,
              justifyContent: 'space-between',
              '& > :not(:first-of-type)': { ml: 0 },
            }
            : undefined
        }
      >
        <Button
          onClick={onCancel}
          color="primary"
          sx={stickyActions ? { flex: 1, borderRadius: '4px', py: 1.2, ml: 0 } : undefined}
        >
          Cancel
        </Button>
        <Button
          onClick={onAction}
          color={actionButtonColor}
          variant={actionButtonVariant}
          autoFocus
          sx={stickyActions ? { flex: 1, borderRadius: '4px', py: 1.2, ml: 0, mr: 0, ...actionButtonSx } : actionButtonSx}
        >
          {actionButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomDialogWithContent;
